import React, {useState, useMemo} from 'react';

import {useMutation} from '@apollo/client/react';
import {splitEmails, isValidEmail} from '../../utils/emailUtils';
import {X, UserPlus, AlertTriangle, CheckCircle} from 'lucide-react';
import {gql} from 'types/__generated__';

/**
 * Only the fields the split flow reads. This modal is opened from both UsersPage and
 * UserDetailPage, whose queries select different shapes, so it cannot borrow either
 * query's generated type without coupling itself to one of them. Every generated user
 * that selects these fields satisfies it structurally.
 */
interface SplittableUser {
  email: string;
  organisationMemberships?: Array<{name: string; organisationId: string}> | null;
  workInformation?: {companyName?: string | null} | null;
}

interface SplitUserModalProps {
  user: SplittableUser;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// createUser returns Boolean! and takes UserInput, which only carries username, organisation
// and role — the new accounts cannot be seeded with the original's name/address/work details.
const CREATE_USER_MUTATION = gql(`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input)
  }
`);

// There is no updateUser; setUserEmail is the admin-facing way to rewrite an account's email.
const SET_USER_EMAIL_MUTATION = gql(`
  mutation SetUserEmail($oldEmail: String!, $newEmail: String!) {
    setUserEmail(oldEmail: $oldEmail, newEmail: $newEmail)
  }
`);

export function SplitUserModal({user, isOpen, onClose, onSuccess}: SplitUserModalProps) {
  const [selectedPrimaryEmail, setSelectedPrimaryEmail] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [createUser] = useMutation(CREATE_USER_MUTATION);
  const [setUserEmail] = useMutation(SET_USER_EMAIL_MUTATION);

  // Parse emails from the malformed email field
  const emails = useMemo(() => {
    return splitEmails(user.email).filter(isValidEmail);
  }, [user.email]);

  // Set default primary email to first email
  React.useEffect(() => {
    if (emails.length > 0 && !selectedPrimaryEmail) {
      setSelectedPrimaryEmail(emails[0]);
    }
  }, [emails, selectedPrimaryEmail]);

  if (!isOpen) return null;

  const handleSplit = async () => {
    if (!selectedPrimaryEmail) {
      setError('Please select a primary email for the existing account');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Rewrite the existing account's malformed email down to the chosen primary one.
      await setUserEmail({
        variables: {
          oldEmail: user.email,
          newEmail: selectedPrimaryEmail,
        },
      });

      // 2. Create new users for all other emails. UserInput only accepts the username and the
      // organisation, so each new account starts empty and is invited by email; profile details
      // have to be filled in afterwards from the user's detail page.
      const otherEmails = emails.filter((email) => email !== selectedPrimaryEmail);
      const organisationId = user.organisationMemberships?.[0]?.organisationId;

      for (const email of otherEmails) {
        await createUser({
          variables: {
            input: {
              username: email,
              organisationId,
            },
          },
        });
      }

      setSuccess(true);

      // Wait a bit before closing to show success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error splitting user:', err);
      setError(err.message || 'Failed to split user accounts');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <UserPlus className="text-blue-600 dark:text-blue-400" size={24} />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Split Malformed User Account</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" disabled={isProcessing}>
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning */}
          <div className="flex items-start space-x-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertTriangle className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Malformed User Account Detected</p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                This user account contains {emails.length} email addresses in a single field. This will create {emails.length - 1} new user account(s)
                and update the existing account with one email.
              </p>
            </div>
          </div>

          {/* Current malformed email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Malformed Email Field</label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-900 dark:text-gray-100 break-all font-mono">{user.email}</p>
            </div>
          </div>

          {/* Detected emails */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Detected Email Addresses ({emails.length})</label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {emails.map((email, index) => (
                <label
                  key={email}
                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedPrimaryEmail === email
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                      : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="primaryEmail"
                    value={email}
                    checked={selectedPrimaryEmail === email}
                    onChange={(e) => setSelectedPrimaryEmail(e.target.value)}
                    disabled={isProcessing}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{email}</p>
                    {selectedPrimaryEmail === email && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Will keep the existing user account</p>
                    )}
                    {selectedPrimaryEmail !== email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Will create a new user account</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* What the new accounts do and do not get */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">New accounts are created empty</p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Each new account is created with its email as the username</li>
              <li>
                • They join{' '}
                {user.organisationMemberships?.[0]?.name ? (
                  <span className="font-medium">{user.organisationMemberships[0].name}</span>
                ) : (
                  'your current organisation'
                )}{' '}
                and are sent an email invitation
              </li>
              <li>
                • Profile details (name{user.workInformation?.companyName ? ', company' : ''}, address, work information) are{' '}
                <span className="font-medium">not</span> copied across — add them from each new user's detail page afterwards
              </li>
            </ul>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertTriangle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="flex items-start space-x-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <CheckCircle className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">Success!</p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Users have been split successfully. Created {emails.length - 1} new account(s).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSplit}
            disabled={isProcessing || !selectedPrimaryEmail || success}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Splitting Users...</span>
              </>
            ) : (
              <>
                <UserPlus size={18} />
                <span>Split into {emails.length} Users</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
